import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import { CalendarIcon, Check, Minus, Plus, Loader2, BadgeCheck, PartyPopper } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Receipt from "./Receipt";
import { MENUS, SERVING_TYPES, NATMAD_ADDITIONS, TILVALG_SECTIONS, BORDE, STROEM_TILVALG, EVENT_TYPES, buildSummary, slug } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const EASE = [0.22, 1, 0.36, 1];

function StepLabel({ n, children }) {
    return (
        <p className="mb-5 font-mono text-xs uppercase tracking-[0.25em] text-poelse">
            Trin {n} — {children}
        </p>
    );
}

function FieldError({ msg, id }) {
    if (!msg) return null;
    return (
        <p data-testid={id} className="mt-2 text-xs font-semibold text-rust">
            {msg}
        </p>
    );
}

export default function Configurator({ menuId, onMenuChange }) {
    const [servingType, setServingType] = useState("almindelig");
    const [natmadAddition, setNatmadAddition] = useState(null);
    const [guests, setGuests] = useState(40);
    const [date, setDate] = useState(null);
    const [dateOpen, setDateOpen] = useState(false);
    const [tilvalgSet, setTilvalgSet] = useState({});
    const [bordeQty, setBordeQty] = useState({});
    const [stroemSet, setStroemSet] = useState({});
    const [deselected, setDeselected] = useState({});
    const [eventType, setEventType] = useState("");
    const [fields, setFields] = useState({ name: "", phone: "", email: "", message: "" });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);

    useEffect(() => {
        setDeselected({});
        setNatmadAddition(null);
    }, [menuId]);

    const { menu, lines, additions, menuFravalg } = useMemo(
        () => buildSummary(servingType, menuId, natmadAddition, guests, tilvalgSet, bordeQty, stroemSet, deselected),
        [servingType, menuId, natmadAddition, guests, tilvalgSet, bordeQty, stroemSet, deselected]
    );

    const setField = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));
    const toggle = (setter) => (name) => (v) => setter((s) => ({ ...s, [name]: !!v }));

    const chooseServingType = (id) => {
        setServingType(id);
        setNatmadAddition(null);
        if (id === "natmad") {
            if (menuId && !["menu1", "menu2"].includes(menuId)) onMenuChange(null);
            setTilvalgSet({});
            setBordeQty({});
            setStroemSet({});
        }
    };

    const visibleMenus = servingType === "natmad" ? MENUS.slice(0, 2) : MENUS;

    const validate = () => {
        const errs = {};
        if (!menuId) errs.menu = "Vælg en menu";
        if (servingType === "natmad" && menuId === "menu1" && !natmadAddition) errs.natmad = "Vælg præcis ét tilvalg til natmad";
        if (!date) errs.date = "Vælg en dato";
        if (guests < 10) errs.guests = "Mindst 10 gæster";
        if (fields.name.trim().length < 2) errs.name = "Skriv dit navn";
        if (!/^[+\d][\d\s\-()]{6,}$/.test(fields.phone.trim())) errs.phone = "Skriv et gyldigt telefonnummer";
        if (fields.email.trim() && !/^\S+@\S+\.\S+$/.test(fields.email.trim())) errs.email = "Skriv en gyldig e-mail";
        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            toast.error("Udfyld de markerede felter");
            return;
        }
        setErrors({});
        setSubmitting(true);
        try {
            await axios.post(`${API}/bookings`, {
                menu: menu.name,
                serving_type: servingType,
                natmad_addition: servingType === "natmad" ? natmadAddition : null,
                guests,
                date: format(date, "yyyy-MM-dd"),
                additions,
                event_type: eventType || null,
                menu_fravalg: menuFravalg,
                name: fields.name.trim(),
                phone: fields.phone.trim(),
                email: fields.email.trim() || null,
                message: fields.message.trim(),
            });
            setDone({ name: fields.name.trim(), date });
            toast.success("Din forespørgsel er sendt!");
        } catch {
            toast.error("Noget gik galt — prøv igen om et øjeblik");
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setDone(null);
        onMenuChange(null);
        setServingType("almindelig");
        setNatmadAddition(null);
        setDate(null);
        setTilvalgSet({});
        setBordeQty({});
        setStroemSet({});
        setDeselected({});
        setEventType("");
        setFields({ name: "", phone: "", email: "", message: "" });
    };

    return (
        <section id="byg-din-event" data-testid="configurator-section" className="grain relative py-28 sm:py-36">
            <div className="mx-auto max-w-6xl px-5">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="mb-16 max-w-xl"
                >
                    <h2 className="font-serif text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
                        Sammensæt den menu der ønskes.
                    </h2>
                    <p className="mt-4 text-base text-muted-foreground sm:text-lg">
                        Udfyld rubrikkerne –
                        <br />
                        Forespørgslen er <strong className="text-ink">100% uforpligtende</strong>.
                        <br />
                        Jørgen ringer dig op med et tilbud og et hurtigt svar.
                    </p>
                </motion.div>

                {done ? (
                    <motion.div
                        data-testid="booking-success"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, ease: EASE }}
                        className="mx-auto max-w-xl bg-paper p-10 text-center shadow-[0_20px_60px_rgba(11,42,51,0.12)] ring-1 ring-border"
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.15 }}
                            className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-full bg-sky-soft text-poelse"
                        >
                            <PartyPopper className="h-7 w-7" />
                        </motion.div>
                        <h3 className="font-serif text-2xl font-bold text-ink">Tak, {done.name}!</h3>
                        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                            Din forespørgsel til{" "}
                            <strong className="text-ink">{format(done.date, "d. MMMM yyyy", { locale: da })}</strong>{" "}
                            er sendt afsted. Jørgen ringer dig op med et tilbud — og husk: den er
                            100% uforpligtende.
                        </p>
                        <button
                            data-testid="new-booking-button"
                            onClick={reset}
                            className="mt-8 rounded-full border border-ink/15 px-6 py-3 text-sm font-semibold text-ink transition-[border-color] duration-200 hover:border-ink/40"
                        >
                            Lav en ny forespørgsel
                        </button>
                    </motion.div>
                ) : (
                    <div className="grid items-start gap-10 lg:grid-cols-[1fr_360px]">
                        <motion.form
                            onSubmit={handleSubmit}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.8, ease: EASE }}
                            className="space-y-12 bg-paper p-6 shadow-[0_20px_60px_rgba(11,42,51,0.08)] ring-1 ring-border sm:p-10"
                        >
                            <div>
                                <StepLabel n={1}>Vælg servering / Natmad</StepLabel>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    {SERVING_TYPES.map((s) => {
                                        const active = servingType === s.id;
                                        return (
                                            <button
                                                key={s.id}
                                                type="button"
                                                data-testid={`serving-${s.id}`}
                                                onClick={() => chooseServingType(s.id)}
                                                className={`relative rounded-xl border-2 p-4 text-left transition-[border-color,background-color,transform] duration-200 active:scale-[0.98] ${
                                                    active
                                                        ? "border-poelse bg-sky-soft"
                                                        : "border-border bg-background hover:border-ink/25"
                                                }`}
                                            >
                                                {active && (
                                                    <span className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-poelse text-paper">
                                                        <Check className="h-3.5 w-3.5" />
                                                    </span>
                                                )}
                                                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-poelse">{s.name}</p>
                                                <p className="mt-1 text-sm leading-snug text-muted-foreground">{s.description}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <StepLabel n={2}>Vælg menu</StepLabel>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    {visibleMenus.map((m) => {
                                        const active = menuId === m.id;
                                        return (
                                            <button
                                                key={m.id}
                                                type="button"
                                                data-testid={`config-menu-${m.id}`}
                                                onClick={() => onMenuChange(m.id)}
                                                className={`relative rounded-xl border-2 p-4 text-left transition-[border-color,background-color,transform] duration-200 active:scale-[0.98] ${
                                                    active
                                                        ? "border-poelse bg-sky-soft"
                                                        : "border-border bg-background hover:border-ink/25"
                                                }`}
                                            >
                                                {active && (
                                                    <span className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-poelse text-paper">
                                                        <Check className="h-3.5 w-3.5" />
                                                    </span>
                                                )}
                                                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-poelse">{m.name}</p>
                                                <p className="mt-1 font-serif text-base font-semibold text-ink">{m.tagline}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                                {menu && (
                                    <div
                                        data-testid="menu-contents"
                                        className="mt-5 rounded-xl border border-dashed border-poelse/20 bg-background p-5"
                                    >
                                        <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                                            {menu.name} indeholder — fjern flueben ved det, I ikke ønsker
                                        </p>
                                        <ul className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
                                            {menu.contents.map((item) => (
                                                <li key={item} className="flex items-center gap-2.5">
                                                    <Checkbox
                                                        id={`mi-${slug(item)}`}
                                                        data-testid={`menu-item-${slug(item)}`}
                                                        checked={!deselected[item]}
                                                        onCheckedChange={(v) =>
                                                            setDeselected((s) => ({ ...s, [item]: !v }))
                                                        }
                                                    />
                                                    <label
                                                        htmlFor={`mi-${slug(item)}`}
                                                        className={`cursor-pointer text-sm transition-colors duration-150 ${
                                                            deselected[item] ? "text-muted-foreground line-through" : "text-ink"
                                                        }`}
                                                    >
                                                        {item}
                                                    </label>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <FieldError msg={errors.menu} id="error-menu" />
                                {servingType === "natmad" && menuId === "menu1" && (
                                    <div
                                        data-testid="natmad-addition"
                                        className="mt-5 rounded-xl border border-dashed border-poelse/30 bg-background p-5"
                                    >
                                        <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                                            Tilvalg til natmad — vælg præcis ét
                                        </p>
                                        <div className="flex flex-wrap gap-3">
                                            {NATMAD_ADDITIONS.map((a) => {
                                                const active = natmadAddition === a.name;
                                                return (
                                                    <button
                                                        key={a.id}
                                                        type="button"
                                                        data-testid={`natmad-addition-${a.id}`}
                                                        onClick={() => setNatmadAddition(a.name)}
                                                        className={`rounded-full border px-4 py-2 text-sm transition-[border-color,background-color] duration-200 ${
                                                            active
                                                                ? "border-poelse bg-sky-soft font-semibold text-ink"
                                                                : "border-border text-muted-foreground hover:border-ink/25"
                                                        }`}
                                                    >
                                                        {a.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <FieldError msg={errors.natmad} id="error-natmad" />
                                    </div>
                                )}
                            </div>

                            <div>
                                <StepLabel n={3}>Gæster & dato</StepLabel>
                                <div className="grid gap-8 sm:grid-cols-2">
                                    <div>
                                        <label className="mb-2 block text-sm font-semibold text-ink" htmlFor="guests-input">
                                            Forventet antal gæster
                                        </label>
                                        <Input
                                            id="guests-input"
                                            data-testid="guests-input"
                                            type="number"
                                            min={10}
                                            max={500}
                                            value={guests}
                                            onChange={(e) => setGuests(Math.max(0, Number(e.target.value)))}
                                            className="mb-4 bg-background"
                                        />
                                        <Slider
                                            data-testid="guests-slider"
                                            value={[Math.min(Math.max(guests, 10), 300)]}
                                            min={10}
                                            max={300}
                                            step={5}
                                            onValueChange={([v]) => setGuests(v)}
                                        />
                                        <FieldError msg={errors.guests} id="error-guests" />
                                    </div>
                                    <div>
                                        <label className="mb-2 block text-sm font-semibold text-ink">Ønsket dato</label>
                                        <Popover open={dateOpen} onOpenChange={setDateOpen}>
                                            <PopoverTrigger asChild>
                                                <button
                                                    type="button"
                                                    data-testid="date-picker-trigger"
                                                    className={`flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm transition-[border-color] duration-200 hover:border-ink/30 ${
                                                        date ? "text-ink" : "text-muted-foreground"
                                                    }`}
                                                >
                                                    {date ? format(date, "d. MMMM yyyy", { locale: da }) : "Vælg dato"}
                                                    <CalendarIcon className="h-4 w-4 text-poelse" />
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={date}
                                                    onSelect={(d) => {
                                                        setDate(d);
                                                        setDateOpen(false);
                                                    }}
                                                    disabled={{ before: new Date() }}
                                                    locale={da}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                        <FieldError msg={errors.date} id="error-date" />
                                    </div>
                                </div>
                            </div>

                            {servingType === "almindelig" && (
                                <div>
                                    <StepLabel n={4}>Tilvalg</StepLabel>
                                    <div className="space-y-10">
                                        {TILVALG_SECTIONS.map((section) => (
                                            <div key={section.id}>
                                                <p className="mb-4 font-serif text-lg font-bold text-ink">{section.title}</p>
                                                <div className={`grid gap-x-8 gap-y-6 ${section.cols}`}>
                                                    {section.groups.map((group) => (
                                                        <div key={group.id}>
                                                            <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                                                                {group.title}
                                                            </p>
                                                            <ul className="space-y-2.5">
                                                                {group.items.map((item) => (
                                                                    <li key={item} className="flex items-center gap-2.5">
                                                                        <Checkbox
                                                                            id={`tv-${slug(item)}`}
                                                                            data-testid={`tilvalg-${slug(item)}`}
                                                                            checked={!!tilvalgSet[item]}
                                                                            onCheckedChange={toggle(setTilvalgSet)(item)}
                                                                        />
                                                                        <label htmlFor={`tv-${slug(item)}`} className="cursor-pointer text-sm text-ink">
                                                                            {item}
                                                                        </label>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}

                                        <div>
                                            <p className="mb-4 font-serif text-lg font-bold text-ink">Praktisk til arrangementet</p>
                                            <div className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                                                <div>
                                                    <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Borde</p>
                                                    <ul className="space-y-3">
                                                        {BORDE.map((b) => (
                                                            <li key={b.id} className="flex items-center justify-between gap-3">
                                                                <span className="text-sm text-ink">{b.name}</span>
                                                                <div className="flex items-center gap-3">
                                                                    <button
                                                                        type="button"
                                                                        data-testid={`borde-minus-${b.id}`}
                                                                        onClick={() => setBordeQty((s) => ({ ...s, [b.id]: Math.max(0, (s[b.id] || 0) - 1) }))}
                                                                        className="grid h-8 w-8 place-items-center rounded-full border border-input text-ink transition-[border-color] duration-200 hover:border-ink/40 active:scale-90"
                                                                    >
                                                                        <Minus className="h-4 w-4" />
                                                                    </button>
                                                                    <span data-testid={`borde-qty-${b.id}`} className="w-6 text-center font-mono text-sm font-bold">
                                                                        {bordeQty[b.id] || 0}
                                                                    </span>
                                                                    <button
                                                                        type="button"
                                                                        data-testid={`borde-plus-${b.id}`}
                                                                        onClick={() => setBordeQty((s) => ({ ...s, [b.id]: (s[b.id] || 0) + 1 }))}
                                                                        className="grid h-8 w-8 place-items-center rounded-full border border-input text-ink transition-[border-color] duration-200 hover:border-ink/40 active:scale-90"
                                                                    >
                                                                        <Plus className="h-4 w-4" />
                                                                    </button>
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div>
                                                    <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Strøm & udstyr</p>
                                                    <ul className="space-y-2.5">
                                                        {STROEM_TILVALG.map((item) => (
                                                            <li key={item} className="flex items-center gap-2.5">
                                                                <Checkbox
                                                                    id={`st-${slug(item)}`}
                                                                    data-testid={`stroem-${slug(item)}`}
                                                                    checked={!!stroemSet[item]}
                                                                    onCheckedChange={toggle(setStroemSet)(item)}
                                                                />
                                                                <label htmlFor={`st-${slug(item)}`} className="cursor-pointer text-sm text-ink">
                                                                    {item}
                                                                </label>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div>
                                <StepLabel n={5}>Dine oplysninger</StepLabel>
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <Select value={eventType} onValueChange={setEventType}>
                                        <SelectTrigger data-testid="event-select" className="bg-background">
                                            <SelectValue placeholder="Hvilken begivenhed? (valgfrit)" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {EVENT_TYPES.map((t) => (
                                                <SelectItem key={t} value={t} data-testid={`event-option-${slug(t)}`}>
                                                    {t}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <div>
                                        <Input
                                            data-testid="name-input"
                                            placeholder="Dit navn"
                                            value={fields.name}
                                            onChange={setField("name")}
                                            className="bg-background"
                                        />
                                        <FieldError msg={errors.name} id="error-name" />
                                    </div>
                                    <div>
                                        <Input
                                            data-testid="phone-input"
                                            type="tel"
                                            placeholder="Dit telefonnummer"
                                            value={fields.phone}
                                            onChange={setField("phone")}
                                            className="bg-background"
                                        />
                                        <FieldError msg={errors.phone} id="error-phone" />
                                    </div>
                                    <div>
                                        <Input
                                            data-testid="email-input"
                                            type="email"
                                            placeholder="Din e-mail (valgfri)"
                                            value={fields.email}
                                            onChange={setField("email")}
                                            className="bg-background"
                                        />
                                        <FieldError msg={errors.email} id="error-email" />
                                    </div>
                                    <Textarea
                                        data-testid="message-input"
                                        placeholder="Fortæl om festen — sted, særlige ønsker..."
                                        value={fields.message}
                                        onChange={setField("message")}
                                        className="bg-background sm:col-span-2"
                                        rows={3}
                                    />
                                </div>
                            </div>

                            <div className="border-t border-dashed border-poelse/20 pt-8">
                                <button
                                    type="submit"
                                    data-testid="submit-booking-button"
                                    disabled={submitting}
                                    className="flex w-full items-center justify-center gap-2 rounded-full bg-poelse py-4 text-base font-bold text-paper transition-[background-color,transform] duration-200 hover:bg-poelse-deep active:scale-[0.99] disabled:opacity-60"
                                >
                                    {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
                                    Send uforpligtende forespørgsel
                                </button>
                                <p className="mt-4 flex items-center justify-center gap-1.5 text-xs font-semibold text-ochre">
                                    <BadgeCheck className="h-4 w-4" />
                                    Udfyld blot navn og telefonnummer — Jørgen ringer dig op
                                </p>
                            </div>
                        </motion.form>
                        <Receipt lines={lines} guests={guests} />
                    </div>
                )}
            </div>
        </section>
    );
}
